/**
 * "Activity per model" — wat de bot deed, waarom, en wanneer.
 *
 * 16-09 (Jordy): "ik wil in het dashboard zelf kunnen zien wat er gebeurt, hoe,
 * waarom en wanneer." Alles hieronder werd al vastgelegd; dit zet het op een
 * rij, per model en per periode, met de instellingen erbij waar het gedrag uit
 * volgt (onze eigen grens, het aantal pogingen, de stappen na een
 * daglimiet-weigering, de minimale tussentijd en het aantal modellen tegelijk).
 */
import { useEffect, useState } from 'react'
import { Panel, Tag } from './ui'
import { useApi, usePoll } from '../lib/useApi'
import { num } from '../lib/format'

interface Gebeurtenis {
  at: string | null
  model_persona_id: number
  model: string
  soort: 'created' | 'waiting' | 'moved' | 'failed'
  post_id: number | null
  platform_post_id: string | null
  tekst: string
  uploads: number | null
}

interface Antwoord {
  hours: number
  counts: Record<string, number>
  settings: {
    eigen_max_per_24u: number | null
    pogingen_per_post: number
    stappen_na_daglimiet: number[]
    min_seconden_tussen_posts: number
    modellen_tegelijk_fc: number
  }
  events: Gebeurtenis[]
}

const KLEUR: Record<string, 'ok' | 'warn' | 'bad' | 'mut' | 'acc'> = {
  created: 'ok', waiting: 'warn', moved: 'acc', failed: 'bad',
}

function klok(iso: string | null): string {
  if (!iso) return '—'
  const ruw = String(iso)
  const d = new Date(ruw.includes('Z') || ruw.includes('+') ? ruw : ruw + 'Z')
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

export function Activiteit({ modellen }: {
  modellen: { model_persona_id: number; name: string }[]
}) {
  const [open, setOpen] = useState(false)
  const [model, setModel] = useState('all')
  const [uren, setUren] = useState('24')
  const [soort, setSoort] = useState('all')
  const pad = `/api/admin/posting/activiteit?hours=${uren}&limit=400`
    + (model !== 'all' ? `&model_persona_id=${model}` : '')
  const data = useApi<Antwoord>(open ? pad : null, [pad, open])
  usePoll(20000, data.refetch, open)
  const [geladen, setGeladen] = useState<Date | null>(null)
  useEffect(() => { if (data.data) setGeladen(new Date()) }, [data.data])
  const s = data.data?.settings
  const rijen = (data.data?.events ?? []).filter((e) => soort === 'all' || e.soort === soort)

  return (
    <Panel
      title="Activity per model"
      desc="Every create, wait, move and failure — with FanCentro's own words"
      right={
        <button className="btn ghost sm" type="button" onClick={() => setOpen((x) => !x)}>
          {open ? 'Hide activity' : 'Show activity'}
        </button>
      }
    >
      {!open ? null : (
        <>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <select className="select" value={model} onChange={(e) => setModel(e.target.value)}>
              <option value="all">All models</option>
              {modellen.map((m) => (
                <option key={m.model_persona_id} value={String(m.model_persona_id)}>{m.name}</option>
              ))}
            </select>
            <select className="select" value={uren} onChange={(e) => setUren(e.target.value)}>
              <option value="6">Last 6 hours</option>
              <option value="24">Last 24 hours</option>
              <option value="72">Last 3 days</option>
              <option value="168">Last 7 days</option>
            </select>
            <select className="select" value={soort} onChange={(e) => setSoort(e.target.value)}>
              <option value="all">Everything</option>
              <option value="created">Created</option>
              <option value="waiting">Waiting</option>
              <option value="moved">Moved</option>
              <option value="failed">Failed</option>
            </select>
            <span className="mut2" style={{ fontSize: 12 }}>
              {Object.entries(data.data?.counts ?? {}).map(([k, v]) => `${num(v)} ${k}`).join(' · ')}
              {geladen ? ` · updated ${geladen.toLocaleTimeString('en-GB')}` : ''}
            </span>
          </div>
          {s ? (
            <div className="mut2" style={{ fontSize: 11, marginBottom: 8, lineHeight: 1.5 }}>
              {s.eigen_max_per_24u
                ? `Current rules: stop at ${s.eigen_max_per_24u} creates per model per 24 h · `
                : 'Current rules: no limit of our own — FanCentro decides · '}
              {`${s.pogingen_per_post} attempt${s.pogingen_per_post === 1 ? '' : 's'} per post · `}
              {`after a daily-limit refusal retry at ${s.stappen_na_daglimiet.join(', ')} minutes · `}
              {`at least ${s.min_seconden_tussen_posts}s between two posts of one model · `}
              {`${s.modellen_tegelijk_fc} models posting at once`}
            </div>
          ) : null}
          <div className="panescroll" style={{ maxHeight: 420 }}>
            {!rijen.length ? (
              <div className="empty" style={{ padding: 18 }}>
                {data.data ? 'Nothing in this window' : 'Loading…'}
              </div>
            ) : rijen.map((e, i) => (
              <div className="feedrow" key={`${e.post_id}-${e.at}-${i}`} style={{ alignItems: 'flex-start' }}>
                <span className="mut2 mono" style={{ fontSize: 11, minWidth: 96 }}>{klok(e.at)}</span>
                <span className="fmodel" style={{ minWidth: 120 }}>{e.model}</span>
                <Tag kind={KLEUR[e.soort] ?? 'mut'}>{e.soort}</Tag>
                <span className="ftext" style={{ fontSize: 12 }}>
                  {e.tekst}
                  <span className="mut2" style={{ display: 'block', fontSize: 11 }}>
                    {e.post_id ? `post ${e.post_id}` : ''}
                    {e.platform_post_id ? ` · ${e.platform_post_id}` : ''}
                    {e.uploads && e.uploads > 1 ? ` · photo uploaded ${e.uploads}×` : ''}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  )
}
